import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult
} from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';
import logger from '../utils/logger';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Handle redirect result (for Google Sign-In redirect flow)
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          logger.log('[Firebase Auth] Google Sign-In via redirect successful:', result.user.email);
        }
      } catch (error) {
        logger.error('[Firebase Auth] Redirect result error:', error);
      }
    };
    handleRedirectResult();
  }, []);

  // Listen to Firebase Auth state changes
  useEffect(() => {
    logger.log('[Firebase Auth] Setting up auth state listener');
    
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in
        const userData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Usuário',
          emailVerified: firebaseUser.emailVerified,
          photoURL: firebaseUser.photoURL
        };
        logger.log('[Firebase Auth] User authenticated:', userData.email);
        setUser(userData);
      } else {
        // User is signed out
        logger.log('[Firebase Auth] User signed out');
        setUser(false);
      }
      setLoading(false);
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  const login = useCallback(async (email, password) => {
    logger.log('[Firebase Auth] Attempting login for:', email);
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      const userData = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Usuário',
        emailVerified: firebaseUser.emailVerified
      };
      
      logger.log('[Firebase Auth] Login successful:', userData.email);
      return userData;
    } catch (error) {
      logger.error('[Firebase Auth] Login error:', error.code, error.message);
      
      // Translate Firebase error codes to Portuguese
      let message = 'Erro ao fazer login';
      switch (error.code) {
        case 'auth/user-not-found':
          message = 'Usuário não encontrado';
          break;
        case 'auth/wrong-password':
          message = 'Senha incorreta';
          break;
        case 'auth/invalid-email':
          message = 'Email inválido';
          break;
        case 'auth/user-disabled':
          message = 'Conta desativada';
          break;
        case 'auth/too-many-requests':
          message = 'Muitas tentativas. Tente novamente mais tarde';
          break;
        case 'auth/invalid-credential':
          message = 'Email ou senha incorretos';
          break;
        default:
          message = error.message;
      }
      throw new Error(message);
    }
  }, []);

  const register = useCallback(async (email, password, name) => {
    logger.log('[Firebase Auth] Attempting registration for:', email);
    
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // Update display name
      if (name) {
        await updateProfile(firebaseUser, { displayName: name });
      }
      
      const userData = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        name: name || firebaseUser.email?.split('@')[0] || 'Usuário',
        emailVerified: firebaseUser.emailVerified
      };
      
      logger.log('[Firebase Auth] Registration successful:', userData.email);
      return userData;
    } catch (error) {
      logger.error('[Firebase Auth] Registration error:', error.code, error.message);
      
      // Translate Firebase error codes to Portuguese
      let message = 'Erro ao registrar';
      switch (error.code) {
        case 'auth/email-already-in-use':
          message = 'Este email já está em uso';
          break;
        case 'auth/invalid-email':
          message = 'Email inválido';
          break;
        case 'auth/weak-password':
          message = 'Senha muito fraca (mínimo 6 caracteres)';
          break;
        case 'auth/operation-not-allowed':
          message = 'Registro desabilitado';
          break;
        default:
          message = error.message;
      }
      throw new Error(message);
    }
  }, []);

  const loginWithGoogle = useCallback(async () => {
    logger.log('[Firebase Auth] Attempting Google Sign-In');
    
    try {
      // Try popup first
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;
      
      const userData = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Usuário',
        emailVerified: firebaseUser.emailVerified,
        photoURL: firebaseUser.photoURL
      };
      
      logger.log('[Firebase Auth] Google Sign-In successful:', userData.email);
      return userData;
    } catch (error) {
      logger.error('[Firebase Auth] Google Sign-In error:', error.code, error.message);
      
      // If popup is blocked or fails due to COOP, try redirect
      if (error.code === 'auth/popup-blocked' || 
          error.code === 'auth/popup-closed-by-user' ||
          error.message?.includes('Cross-Origin-Opener-Policy')) {
        logger.log('[Firebase Auth] Falling back to redirect sign-in');
        await signInWithRedirect(auth, googleProvider);
        return null; // Will be handled by getRedirectResult
      }
      
      // Translate Firebase error codes to Portuguese
      let message = 'Erro ao entrar com Google';
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          message = 'Login cancelado';
          break;
        case 'auth/popup-blocked':
          message = 'Pop-up bloqueado pelo navegador. Permita pop-ups para este site.';
          break;
        case 'auth/cancelled-popup-request':
          message = 'Operação cancelada';
          break;
        case 'auth/account-exists-with-different-credential':
          message = 'Já existe uma conta com este email usando outro método de login';
          break;
        case 'auth/unauthorized-domain':
          message = 'Este domínio não está autorizado para login com Google';
          break;
        default:
          message = error.message;
      }
      throw new Error(message);
    }
  }, []);

  const logout = useCallback(async () => {
    logger.log('[Firebase Auth] Logging out');
    
    try {
      await signOut(auth);
      // Clear any local storage data
      localStorage.removeItem('lojas_seguro_v2');
      localStorage.removeItem('dealer_profile_v1');
      localStorage.removeItem('dealer_pending_lojas');
      localStorage.removeItem('dealer_user_cache');
      localStorage.removeItem('dealer_logged_out');
      logger.log('[Firebase Auth] Logout successful');
    } catch (error) {
      logger.error('[Firebase Auth] Logout error:', error);
      throw error;
    }
  }, []);

  const resetPassword = useCallback(async (email) => {
    logger.log('[Firebase Auth] Sending password reset email to:', email);
    
    try {
      await sendPasswordResetEmail(auth, email);
      logger.log('[Firebase Auth] Password reset email sent successfully');
      return true;
    } catch (error) {
      logger.error('[Firebase Auth] Password reset error:', error.code, error.message);
      
      // Translate Firebase error codes to Portuguese
      let message = 'Erro ao enviar email de recuperação';
      switch (error.code) {
        case 'auth/user-not-found':
          message = 'Nenhuma conta encontrada com este email';
          break;
        case 'auth/invalid-email':
          message = 'Email inválido';
          break;
        case 'auth/too-many-requests':
          message = 'Muitas tentativas. Tente novamente mais tarde';
          break;
        default:
          message = error.message;
      }
      throw new Error(message);
    }
  }, []);

  // authFetch is no longer needed with Firebase Auth, but keep for compatibility
  const authFetch = useCallback(async (url, options = {}) => {
    // For Firebase Auth, we can add the ID token to requests if needed
    if (auth.currentUser) {
      const token = await auth.currentUser.getIdToken();
      return fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${token}`
        }
      });
    }
    return fetch(url, options);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user,
    loading,
    isAuthenticated: !!user && user !== false,
    login,
    loginWithGoogle,
    register,
    logout,
    resetPassword,
    authFetch
  }), [user, loading, login, loginWithGoogle, register, logout, resetPassword, authFetch]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
